
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { Theme } from '../types';
import { useUser } from './UserContext';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [theme, setThemeState] = useState<Theme>('light');

  useEffect(() => {
    const key = user ? `theme_${user.uid}` : 'theme';
    const savedTheme = localStorage.getItem(key) as Theme | null;
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (systemPrefersDark ? 'dark' : 'light');
    setThemeState(initialTheme);
  }, [user]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    const key = user ? `theme_${user.uid}` : 'theme';
    localStorage.setItem(key, theme);
  }, [theme, user]);

  const toggleTheme = useCallback(() => {
    setThemeState(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
